import React from 'react';

import { useContextRequired, useIsMounted } from '@nfctron/nexus-react';

import { Loader } from '@mantine/core';
import Image from 'next/image';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { NoTicketsAvailableAtom } from '@root/cart/cart/_resources/atoms/NoTicketsAvailable.atom';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import { TicketTypeGroupSection } from '../../components/Ticket/TicketTypeCard';
import { cartStoreSelectors, useCartStore, useCartStoreCtx, useTicketTypesApiQuery } from '../../stores/cartStore';

/**
 * Event/Cart/TicketsViewContent atom component
 * @author filipditrich <ditrich@nfctron.com>
 */
export const TicketsViewContentAtom: AtomComponent = () => {
	const { event } = useContextRequired(EventCartPageContext);
	const cartStore = useCartStoreCtx();
	const cartConfig = cartStore.useCartConfig();
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(TicketsViewContentAtom.displayName, cartConfig.eventTypeId),
	);

	/** ticket types */
	const ticketTypesApiQuery = useTicketTypesApiQuery();
	const ticketTypesListedGrouped = useCartStore(cartStoreSelectors.listTicketTypesGrouped, 'deep');
	const hasImportedTicketCarted = useCartStore((store) => cartStoreSelectors.hasSomeImportedCartedTicket(store));
	const isMounted = useIsMounted();
	const isTicketTypesLoading = !isMounted || (ticketTypesApiQuery.isLoading && !ticketTypesApiQuery.hasData);

	return (
		<React.Fragment>
			{/* ticket types */}
			{isTicketTypesLoading ? (
				<div className="flex grow flex-col items-center justify-center gap-8 rounded-xl bg-white p-4">
					{/* hero image */}
					<Image className="mx-auto" src="/static/images/blob.svg" alt={typeSensitiveT('loading.title')} width={300} height={300} />
					{/* loader */}
					<div className="mx-auto">
						<Loader type="dots" size="xl" color="lucky-point" />
					</div>
					<div>
						<p className="text-lucky-point-500 text-center font-bold">{typeSensitiveT('loading.title')}</p>
						<p className="text-center text-base text-zinc-700">{typeSensitiveT('loading.text', { eventName: event.namePub })}</p>
					</div>
				</div>
			) : hasImportedTicketCarted ? (
				<span className="text-base text-zinc-700">{typeSensitiveT('noImportMixTickets.text')}</span>
			) : ticketTypesListedGrouped.length > 0 ? (
				<div className="flex flex-col gap-4">
					{ticketTypesListedGrouped.map((group, _, arr) => {
						return (
							<TicketTypeGroupSection
								key={group.ticketTypeGroup?.ticketTypeGroupId ?? 'default'}
								ticketTypeGroup={group.ticketTypeGroup ?? event.defaultTicketTypeGroup ?? undefined}
								ticketTypes={group.ticketTypes}
								isOnlyGroup={arr.length === 1}
							/>
						);
					})}
				</div>
			) : (
				<div className="flex grow flex-col rounded-lg border border-zinc-200 bg-zinc-50">
					<NoTicketsAvailableAtom />
				</div>
			)}
		</React.Fragment>
	);
};

TicketsViewContentAtom.displayName = 'Event/Cart/TicketsViewContentAtom';
